import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../config/config";
import { toast, ToastContainer } from "react-toastify";

export default function CustomerProfile({ lang = {} }) {
  const [searchParams] = useSearchParams();
  const setupEmailNeeded = searchParams.get("setupEmail") === "true";

  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    phoneNo: "",
    validId: "",
    city: "",
    address: "",
    occupation: "",
    employerName: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = () => {
    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;
    if (!token) return;

    axios
      .get(`${apiKey}/customer-panel/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const p = res.data.profile;
        if (p) {
          setProfile({
            fullName: p.FullName || "",
            email: p.Email || "",
            phoneNo: p.MobilePhoneNo || "",
            validId: p.ValidNationalID || "",
            city: p.City || "",
            address: p.Address || "",
            occupation: p.Occupation || "",
            employerName: p.EmployerName || "",
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;

    if (!token) return;

    setSaving(true);

    axios
      .put(`${apiKey}/customer-panel/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSaving(false);
        toast.success("Profile updated successfully!");

        // If email was updated, trigger initial email setup endpoint if needed
        if (setupEmailNeeded && profile.email) {
          handleSendPasswordReset();
        }
      })
      .catch((err) => {
        setSaving(false);
        console.error(err);
        toast.error("Failed to update profile.");
      });
  };

  const handleSendPasswordReset = () => {
    const session = JSON.parse(sessionStorage.getItem("aman-customer") || "{}");
    const token = session.token;

    if (!token || !profile.email) {
      toast.error("Please enter a valid email address first.");
      return;
    }

    setSendingReset(true);

    axios
      .post(
        `${apiKey}/portal-auth/update-initial-email`,
        { email: profile.email },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setSendingReset(false);
        toast.success(res.data.Message);
      })
      .catch((err) => {
        setSendingReset(false);
        console.error(err);
        toast.error("Failed to send password reset email.");
      });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-3">
      <ToastContainer />
      <div className="pagetitle mb-4">
        <h1 className="fw-bold">{lang.Profile || "My Profile"}</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item active">Profile</li>
          </ol>
        </nav>
      </div>

      {setupEmailNeeded && (
        <div className="alert alert-warning alert-dismissible fade show mb-4 rounded-3 shadow-sm" role="alert">
          <h5 className="alert-heading fw-bold">
            <i className="fa fa-triangle-exclamation me-2"></i> Update Your Email Address
          </h5>
          <p className="mb-0 small">
            Please add your email address below and save your profile. A password reset link will be sent to your email to allow you to set a custom password.
          </p>
        </div>
      )}

      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-header bg-white py-3 border-bottom">
              <h5 className="fw-bold m-0 text-dark">
                <i className="fa fa-user-gear me-2 text-primary"></i> Personal Account Details
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Full Name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6"
                      name="fullName"
                      value={profile.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">National ID / Resident ID</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6 bg-light"
                      value={profile.validId}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Email Address</label>
                    <input
                      type="email"
                      className="form-control form-control-lg fs-6"
                      name="email"
                      placeholder="name@example.com"
                      value={profile.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Mobile Phone Number</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6"
                      name="phoneNo"
                      value={profile.phoneNo}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={profile.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address"
                      value={profile.address}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Occupation</label>
                    <input
                      type="text"
                      className="form-control"
                      name="occupation"
                      value={profile.occupation}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold small text-secondary">Employer Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="employerName"
                      value={profile.employerName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12 d-flex justify-content-between align-items-center mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-primary fw-bold"
                      onClick={handleSendPasswordReset}
                      disabled={sendingReset}
                    >
                      {sendingReset ? "Sending..." : "Send Password Reset Link to Email"}
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-5 fw-bold shadow-sm"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Profile Updates"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
